import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Compass, Map, FileText, Layers, Palette, Shield, ArrowRight, Gavel } from "lucide-react";
import Seo from "@/components/Seo";
import { combineJsonLd, organizationJsonLd, faqJsonLd } from "@/lib/structuredData";

const TOOLS = [
  { to: "/chat", icon: MessageCircle, title: "AI-rådgivare", desc: "Ställ frågor om bygglov, PBL & BBR." },
  { to: "/guide/wizard", icon: Gavel, title: "Regelmotor (PBL/BBR)", desc: "Exakt svar baserat på lag – ej AI-gissning.", badge: "Nytt" },
  { to: "/guide/permit", icon: Compass, title: "Behöver jag bygglov?", desc: "Få AI-bedömning på sekunder." },
  { to: "/guide/property", icon: Map, title: "Fastighetsanalys", desc: "Detaljplan, byggrätter och förutsättningar." },
  { to: "/guide/templates", icon: FileText, title: "Handlingsmallar", desc: "Kontrollplan, ansökan, anmälan." },
  { to: "/guide/review", icon: Layers, title: "Ritningsgranskning", desc: "Ladda upp och få AI-feedback." },
  { to: "/tools/draw", icon: Palette, title: "Ritningsskapare", desc: "Skapa altan- och garageritningar." },
  { to: "/anmal-tillsyn", icon: Shield, title: "Anmäl tillsyn", desc: "Misstänkt olovligt bygge eller avvikelse." },
];

export default function GuideHub() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 animate-fade-in-up">
      <Seo
        title="Bygglovsguide – alla verktyg på ett ställe"
        description="Behöver jag bygglov? Använd Bygglovskompassens guide för att kontrollera lovplikt, granska ritningar, hitta handlingsmallar och analysera fastighetens byggrätt. Allt kostnadsfritt."
        path="/guide"
        keywords="bygglovsguide, behöver jag bygglov, byggrätt, fastighetsanalys, ritningsgranskning"
        jsonLd={combineJsonLd(organizationJsonLd, faqJsonLd)}
      />
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Bygglovsguide</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
        Alla verktyg på ett ställe.
      </h1>
      <p className="mt-3 text-zinc-600 max-w-xl">Välj ett verktyg för att komma vidare i ditt byggprojekt.</p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {TOOLS.map((t, i) => (
          <Link key={t.to} to={t.to} data-testid={`tool-${i}`}
            className="group relative bg-white border border-zinc-200 rounded-sm p-6 hover:border-blue-400 transition-all duration-200 hover:-translate-y-[1px]">
            {t.badge && (
              <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider font-semibold bg-emerald-600 text-white px-2 py-0.5 rounded-sm">
                {t.badge}
              </span>
            )}
            <t.icon className="w-7 h-7 text-blue-700 stroke-[1.5]" />
            <h3 className="mt-5 text-xl font-medium group-hover:text-blue-700 transition" style={{ fontFamily: "Outfit" }}>
              {t.title}
            </h3>
            <p className="mt-2 text-sm text-zinc-600">{t.desc}</p>
            <ArrowRight className="mt-4 w-4 h-4 text-zinc-400 group-hover:text-blue-700 transition" />
          </Link>
        ))}
      </div>
    </div>
  );
}
